import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import { UserAuthProvider } from './context/UserAuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UserLogin from './components/UserLogin';
import UserDashboard from './components/UserDashboard';
import Magazine from './components/Magazine';
import Library from './components/Library';
import Fun from './components/Fun';
import GiftingSecured from './components/GiftingSecured';
import Home from './components/Home';
import Schools from './components/Schools';
import PricingAndPlans from './components/PricingAndPlans';
import AboutUs from './components/AboutUs';
import Contact from './components/Contact';
import SuperDuper from './components/superduper';
import PaymentSuccess from './components/PaymentSuccess';
import TermsAndConditions from './components/TermsAndConditions';
import CancellationRefundPolicy from './components/CancellationRefundPolicy';
import PrivacyPolicy from './components/PrivacyPolicy';
import Quizzes from './components/Quizzes';
import DynamicQuiz from './components/DynamicQuiz';
import ScrollToTop from './components/shared/ScrollToTop';
import SnowfallEffect from './components/shared/SnowfallEffect';
import { useAuth } from './context/AuthContext';
import { useUserAuth } from './context/UserAuthContext';
import { Box, CircularProgress } from '@mui/material';
import './App.css';

const theme = createTheme({
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h2: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 500,
    },
  },
  palette: {
    primary: {
      main: '#FF6B35',
    },
    secondary: {
      main: '#4CAF50',
    },
    background: {
      default: '#ffffff',
    },
  },
});

// Protected Route component for admin
const ProtectedRoute = ({ children }) => {
  const { token, authReady } = useAuth();
  if (!token) {
    return <Navigate to="/yploginadmin" />;
  }
  if (!authReady) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }
  return children;
};

// Protected Route component for users
const ProtectedUserRoute = ({ children }) => {
  const { isAuthenticated, loading } = useUserAuth();

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 30%, #2a2a2a 70%, #1a1a1a 100%)'
        }}
      >
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/user-login" />;
  }

  return children;
};

function App() {
  // Enable snowfall during festive season (Dec 15 - Jan 7)
  const isHolidaySeason = () => {
    const now = new Date();
    const month = now.getMonth(); // 0-11 (0 = January, 11 = December)
    const day = now.getDate();
    return (month === 11 && day >= 15) || (month === 0 && day <= 7);
  };

  return (
    <AuthProvider>
      <UserAuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {/* Festive Snowfall Effect */}
          <SnowfallEffect enabled={isHolidaySeason()} snowflakeCount={50} />
          
          <Router>
            <ScrollToTop />
            <Routes>
            
            <Route path="/yploginadmin" element={<Login />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/pricing" element={
             
                <PricingAndPlans />
            
            } />
            <Route path="/about" element={
            
                <AboutUs />
             
            } />
            <Route path="/contact" element={
             
                <Contact />
             
            } />
            <Route path="/schools" element={
             
                <Schools />
             
            } />
            <Route 
              path="/super-duper" 
              element={
                <ProtectedRoute>
                  <SuperDuper />
                </ProtectedRoute>
              } 
            />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            
            {/* Protected Quiz Routes */}
            <Route 
              path="/quizzes" 
              element={
                <ProtectedUserRoute>
                  <Quizzes />
                </ProtectedUserRoute>
              } 
            />
            <Route 
              path="/quiz/:quizId" 
              element={
                <ProtectedUserRoute>
                  <DynamicQuiz />
                </ProtectedUserRoute>
              } 
            />
            
            {/* User Authentication Routes */}
            <Route path="/user-login" element={<UserLogin />} />
            <Route 
              path="/user-dashboard" 
              element={
                <ProtectedUserRoute>
                  <UserDashboard />
                </ProtectedUserRoute>
              } 
            />
            <Route 
              path="/magazine" 
              element={
                <ProtectedUserRoute>
                  <Magazine />
                </ProtectedUserRoute>
              } 
            />
            <Route 
              path="/library" 
              element={
                <ProtectedUserRoute>
                  <Library />
                </ProtectedUserRoute>
              } 
            />
            <Route 
              path="/fun" 
              element={
                <ProtectedUserRoute>
                  <Fun />
                </ProtectedUserRoute>
              } 
            />
            <Route 
              path="/gifting-secured" 
              element={
                <ProtectedUserRoute>
                  <GiftingSecured />
                </ProtectedUserRoute>
              } 
            />
            
            <Route path="/" element={<Home/>} />
          </Routes>
        </Router>
      </ThemeProvider>
      </UserAuthProvider>
    </AuthProvider>
  );
}

export default App;
